import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import styles from "./ExtraUsagePanel.module.css";
import { describeMeasurementTime } from "../../../utils/credits";
import {
  getAutoReload,
  putAutoReload,
  createExtraUsageCheckout,
} from "../../../services/billingApi";

export const AUTO_RELOAD_DISABLED_MESSAGE =
  "Auto-reload was turned off: your card was declined. Buy hours manually or update your card in Manage Billing.";
export const MIN_CAP_DOLLARS = 10;
export const MAX_CAP_DOLLARS = 500;

function dollars(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

// Extra hours (prepaid, never expire) + opt-in auto-reload. Hidden unless
// GET users/auto-reload succeeds: 403 = not eligible, 404 = feature off.
export default function ExtraUsagePanel({ onLoaded }) {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [capDollars, setCapDollars] = useState("50");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  function applyResult(result) {
    setData(result);
    setEnabled(Boolean(result?.auto_reload?.enabled));
    const cap = result?.auto_reload?.monthly_cap_cents;
    if (typeof cap === "number") setCapDollars(String(Math.round(cap / 100)));
  }

  useEffect(() => {
    let active = true;
    getAutoReload()
      .then((result) => {
        if (!active) return;
        applyResult(result);
        setStatus("ready");
        if (onLoaded) onLoaded(Number(result?.extra_credits || 0));
      })
      .catch(() => {
        if (active) setStatus("hidden");
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleBuy() {
    setError("");
    setBusy(true);
    try {
      const sessionId = await createExtraUsageCheckout();
      const stripe = await loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);
      await stripe.redirectToCheckout({ sessionId });
    } catch {
      setError("Could not start checkout. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    setError("");
    setSaved(false);
    const value = Number(capDollars);
    if (!Number.isInteger(value) || value < MIN_CAP_DOLLARS || value > MAX_CAP_DOLLARS) {
      setError(`Enter a monthly limit between $${MIN_CAP_DOLLARS} and $${MAX_CAP_DOLLARS}.`);
      return;
    }
    setBusy(true);
    try {
      const result = await putAutoReload({ enabled, monthly_cap_cents: value * 100 });
      applyResult(result);
      setSaved(true);
    } catch {
      setError("Could not save auto-reload. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (status !== "ready" || !data) return null;

  const extraCredits = Number(data.extra_credits || 0);
  const capCents = data.auto_reload?.monthly_cap_cents;

  return (
    <section className={styles.panel} aria-label="Extra hours">
      <h5>Extra hours</h5>
      <p>
        Extra balance: {describeMeasurementTime(extraCredits)} (
        {extraCredits.toLocaleString("en-US")} credits). Extra hours never expire and are used
        after your included hours.
      </p>
      {data.auto_reload?.disabled_reason && (
        <div role="alert" className={styles.alert}>
          {AUTO_RELOAD_DISABLED_MESSAGE}
        </div>
      )}
      <div className={styles.row}>
        <button type="button" className="btn btn-primary" onClick={handleBuy} disabled={busy}>
          Buy 15 hours — $10
        </button>
      </div>
      <div className={styles.row}>
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            disabled={busy}
          />{" "}
          Auto-reload 15 hours ($10) when under 1 hour is left
        </label>
      </div>
      <div className={styles.row}>
        <label htmlFor="auto-reload-cap">Monthly auto-reload limit ($)</label>
        <input
          id="auto-reload-cap"
          className={styles["cap-input"]}
          type="number"
          min={MIN_CAP_DOLLARS}
          max={MAX_CAP_DOLLARS}
          step="1"
          value={capDollars}
          onChange={(event) => setCapDollars(event.target.value)}
          disabled={busy}
        />
        <button type="button" className="btn btn-secondary" onClick={handleSave} disabled={busy}>
          Save auto-reload
        </button>
      </div>
      {typeof capCents === "number" && (
        <p className={styles.muted}>
          {`Auto-reload this period: ${dollars(data.auto_reload_spend_cents)} of ${dollars(capCents)}`}
          {data.auto_reload_pending ? " · A reload is in progress." : ""}
        </p>
      )}
      {saved && <p className={styles.muted}>Saved.</p>}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </section>
  );
}
